// The ceremony frame (docs/08-app-design.md 5.1 and 5.2; app/art/welcome/
// PLAN.md section 4.4): /create and /restore in the entrance's frame, with
// no art on any step. A top row with the way back on the left and "Step n
// of N" on the right, then the step rail in progress mode, then the step.
// - back: 'welcome' before any words exist (a plain link back to /welcome);
//   'cancel' once they do, a quiet link whose accessible name says that
//   leaving discards them (the page unmounts, which ends the worker that
//   holds them); none from the moment the setup is saved on this device
//   (installSetup resolved), whatever step is shown and even if a later
//   step fails (R67; SetupSaved below);
// - busy: the passphrase step is saving the setup; Cancel is inert until it
//   settles, so leaving cannot end the worker the session is about to adopt
//   (browser Back is not held off: a page that has gone by the time the save
//   reaches installSetup abandons it there, as Cancel would, and ends its
//   worker; past that point the save commits and the session adopts it);
// - secret: words or a passphrase exist on this step, so the header's links
//   go and its wordmark is plain text; one header click cannot silently
//   discard them (EntranceFrame, SiteHeader). The network pill stays;
// - the one status line follows the step's own actions, then the optional
//   cross-link to the other door;
// - step: a new step starts at the top of the page, rail and all, as a new
//   route does (the steps share one route, so App.tsx's reset never runs);
//   and, as a route change moves focus to main, a new step moves it to its
//   h1, so the heading is read: unless a field the step autofocused already
//   holds it. The control that was pressed has usually unmounted by then,
//   which leaves focus on the body.
// On phones the rail's labels are hidden visually and the counter names the
// stop instead ("Step 1 of 5 · Words").

import { type ReactNode, useLayoutEffect, useRef } from 'react';
import { Link } from 'wouter';
import { markSetupSaved } from '../savedSetup';
import { session } from '../session';
import { EntranceFrame, EntranceStatus } from './EntranceFrame';
import { StatusLine, formatUnits } from './primitives';
import { type Flow, STEPS, StepRail } from './StepRail';

/**
 * A suggestion for the "You will need" lists of Welcome and Create, before any engine exists: about 0.01 SOL in another
 * wallet for the fee reserve. It is never a minimum and never a gate: Activate's own figure is the engine's live
 * `activationCost()` (docs/18-one-fee-reserve.md 5.1 A), which the activation panel shows as what the step needs.
 */
export const SUGGESTED_FUNDING_LAMPORTS = 10_000_000n;
/** `SUGGESTED_FUNDING_LAMPORTS` in SOL, for the copy ("about 0.01 SOL"); never typed in. */
export const SUGGESTED_FUNDING_SOL = formatUnits(SUGGESTED_FUNDING_LAMPORTS, 9).text;

function BackArrow() {
  return (
    <svg viewBox="0 0 14 14" width="13" height="13" aria-hidden="true" focusable="false">
      <path d="M12 7H2M6 3 2 7l4 4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Leave the flow for /welcome; the accessible name says the words go with it. Inert while `busy`. */
export function CancelLink({ className = 'cta-quiet', busy }: { className?: string; busy?: boolean }) {
  return (
    <Link
      href="/welcome"
      className={className}
      aria-label="Cancel and discard these words"
      aria-disabled={busy ? 'true' : undefined}
      onClick={(e) => {
        if (busy) e.preventDefault();
      }}
    >
      Cancel
    </Link>
  );
}

/**
 * After the setup is saved (installSetup resolved), a later step failed (R67). The setup stays on this device, so the
 * screen says so and offers the one way forward that resumes with it; there is no Cancel, no way back and no
 * passphrase form any more, since nothing is left to discard and a second save would meet `DeviceOccupied`.
 * - 'retry': the session holds the keys; Try again reruns what was left (`label` says what that was);
 * - 'continue': the session holds the keys and nothing is left but the overview (defensive: no current path reaches it,
 *   since adoption fails only before the session takes the keys, and Restore of an absent vault runs no later reads);
 * - 'unlock': the session does not hold them (adoption failed, or the app has locked since); /locked opens the saved
 *   setup with the passphrase just set;
 * - 'reload': the session does not hold them and could not read the saved setup back from storage either (it then
 *   reports an empty device); a full page load of /locked runs the session's start again, which reads storage afresh.
 */
export type SavedNext = 'retry' | 'continue' | 'unlock' | 'reload';

/** The status line's sentence when the app locked itself (idle or hidden tab) while the saved screen waited. */
export const LOCKED_WHILE_WAITING = 'The app locked itself while this page waited.';
/** The status line's sentence when the session could not read the saved setup back (SavedNext 'reload'). */
export const SAVED_SETUP_UNREAD = 'This page could not read the saved setup back from this device’s storage.';

/**
 * A failure after saving, as a sentence for the "Not finished." status line: `lead` says in words which step failed,
 * and the raw error follows as its detail. The session's bare "locked" (session.ts, thrown by `engine()` once the app
 * has locked) becomes LOCKED_WHILE_WAITING.
 */
export function savedStepError(e: unknown, lead: string): string {
  const raw = (e instanceof Error ? e.message : String(e)).trim();
  if (raw === 'locked') return LOCKED_WHILE_WAITING;
  if (!raw) return lead;
  return `${lead} Details: ${raw}${/[.!?]$/.test(raw) ? '' : '.'}`;
}

/** A full page load of /locked: the session starts again and reads storage afresh (SavedNext 'reload'). */
export function reloadToLocked(): void {
  window.location.assign('/locked');
}

// The "saving" and "saved but unread" guards (R67 residual: the double fault) live in their own store, savedSetup.ts,
// so the header's network pill reads them too; re-exported here for the pages.
export { markSetupNotSaved, markSetupSaved, markSetupSavedAfter, markSetupSaving, useSavedSetup, useSavedSetupUnread } from '../savedSetup';

/**
 * A save this page load started is committing, or the session is opening the setup it saved, while this screen was
 * reached some other way (browser Back during the save, then Forward or a door; savedSetup.ts 'saving'). Not the fresh
 * doors, since a setup is being stored, and no claim that it failed: the session opens it in a moment, and the route
 * guards then move on to /vault (or /locked's door, should the opening fail).
 */
export function SetupSaving() {
  return (
    <>
      <h1>Saving your setup on this device.</h1>
      <StatusLine state="pending" word="Saving.">
        The setup you started is being saved and opened.
      </StatusLine>
      <p className="b">Nothing needs doing meanwhile. The vault then opens here, or, if this page cannot open it, the unlock screen asks for the passphrase you just set.</p>
    </>
  );
}

/**
 * The saved screen's way to /locked when the session does not hold the keys (SavedNext 'unlock'). Waits for the
 * session's re-read of storage (`relock`, started when the failure was caught); `session.lock()` maps a failed read
 * to 'no-vault', so if the state is not 'locked' the re-read runs once more. Resolves to what the caller should do:
 * open /locked or /vault, or, when storage still cannot be read, stay and offer `reloadToLocked` ('unread'), never a
 * navigation to /welcome or /locked as if the device were empty.
 */
export async function reopenSaved(relock: Promise<void> | null): Promise<'locked' | 'unlocked' | 'unread'> {
  await relock?.catch(() => {});
  let { state } = session.get();
  if (state === 'unlocked') return 'unlocked';
  if (state !== 'locked') {
    await session.lock().catch(() => {});
    state = session.get().state;
  }
  if (state === 'locked') return 'locked';
  // stored but unread: keep the guard armed whatever cleared it meanwhile
  markSetupSaved();
  return 'unread';
}

/** `detail`, when given, sits between the explanation and the way forward (Restore: what is known of a key refresh the lock stopped). */
export function SetupSaved({ error, next, busy, onNext, label, detail }: { error: string; next: SavedNext; busy: boolean; onNext?: () => void; label?: string; detail?: ReactNode }) {
  return (
    <>
      <h1>Your setup is saved on this device.</h1>
      <StatusLine state="failed" word="Not finished.">
        {error}
      </StatusLine>
      <p className="b">
        {next === 'unlock'
          ? 'The vault’s keys are sealed on this device under the passphrase you just set. This page no longer holds them open, so unlock with that passphrase to carry on; nothing needs setting up again.'
          : next === 'reload'
            ? 'The vault’s keys are sealed on this device under the passphrase you just set; nothing needs setting up again. Reloading the app reads this device’s storage afresh and opens the unlock screen for that passphrase.'
            : `The vault’s keys are sealed on this device under the passphrase you just set; leaving this page does not remove them. What did not finish is the step after saving${label ? `: ${label}` : ''}.`}
      </p>
      {detail}
      <div className="entry-actions">
        <button type="button" className="cta cta-mint" onClick={next === 'reload' ? reloadToLocked : onNext} disabled={busy} aria-busy={busy ? 'true' : undefined}>
          {next === 'reload' ? 'Reload and unlock' : next === 'unlock' ? 'Unlock' : next === 'continue' ? 'Continue' : busy ? 'Trying again' : 'Try again'}
        </button>
      </div>
    </>
  );
}

export function CeremonyFrame({
  back,
  flow,
  current,
  wide,
  secret,
  busy,
  after,
  step,
  children,
}: {
  back: 'welcome' | 'cancel' | null;
  flow: Flow;
  /** 1-based stop on the rail. */
  current: number;
  /** 760 px: the phrase, the confirmation and Restore's words. */
  wide?: boolean;
  secret?: boolean;
  /** The step is saving the setup: Cancel does nothing until it settles. */
  busy?: boolean;
  /** Below the status line: the cross-link to the other door, on a flow's first step. */
  after?: ReactNode;
  /** The screen shown (the flow's own step name); when it changes, the page returns to its top and focus to its h1. */
  step: string;
  children: ReactNode;
}) {
  const steps = STEPS[flow];
  const first = useRef(true);
  const frame = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    window.scrollTo(0, 0);
    // after this frame's autofocus has run: only a focus that was lost, or left on main, moves to the heading
    const id = requestAnimationFrame(() => {
      const active = document.activeElement;
      const lost = !active || active === document.body || !active.isConnected || active.id === 'main';
      if (!lost) return;
      const h1 = frame.current?.querySelector('h1');
      if (!h1) return;
      h1.setAttribute('tabindex', '-1');
      h1.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [step]);
  return (
    <EntranceFrame width={wide ? 'wide' : 'read'} secret={secret}>
      <div className="cer" ref={frame}>
        <div className="cer-top">
          {back === 'welcome' ? (
            <Link href="/welcome" className="back">
              <BackArrow /> Welcome
            </Link>
          ) : back === 'cancel' ? (
            <CancelLink className="back cta-quiet" busy={busy} />
          ) : (
            <span className="back" aria-hidden="true" />
          )}
          <span className="count">
            Step {current} of {steps.length}
            <span className="ph"> · {steps[current - 1]}</span>
          </span>
        </div>
        <StepRail flow={flow} current={current} />
        {children}
        <EntranceStatus link={false} />
        {after}
      </div>
    </EntranceFrame>
  );
}
